import { v4 as uuidv4 } from 'uuid';
import {
	CallParty,
	Contact,
	ContactGroup,
	ContactReference,
	ContactSource,
	ExtensionContact,
	ExtensionParty,
	ExternalContact,
	isExtensionContact,
	ManualContact
} from '../interfaces/contact.interface';
import { ExtensionPhoneState } from './extension-contacts-utils';
import { TranslateService} from "@ngx-translate/core";
import { IUser } from 'core-client/api';

export class ContactUtils {

	// Type guards for contact types
	static isExtensionContact(contact: Contact): contact is ExtensionContact {
		return contact.source === ContactSource.EXTENSION;
	}

	static isManualContact(contact: Contact): contact is ManualContact {
		return contact.source === ContactSource.MANUAL;
	}

	static isExternalContact(contact: Contact): contact is ExternalContact {
		return contact.source === ContactSource.LDAP || contact.source === ContactSource.OUTLOOK;
	}

	static getExtensionStatus(contact: Contact): number | undefined {
		return isExtensionContact(contact) ? contact.status : undefined;
	}

	static isExtensionIdle(statusCode: number): boolean {
		return statusCode === ExtensionPhoneState.Idle
	}

	static isExtensionRinging(statusCode: number): boolean {
		return statusCode === ExtensionPhoneState.Ringing
	}

	static isExtensionInUse(statusCode: number): boolean {
		return (statusCode > ExtensionPhoneState.Idle && statusCode !== ExtensionPhoneState.Unavailable && statusCode !== ExtensionPhoneState.Ringing)
	}

	static isExtensionUnavailable(statusCode: number): boolean {
		return statusCode ===  ExtensionPhoneState.Unavailable;
	}

	static isExtensionUnknown(statusCode: number): boolean {
		return statusCode ===  ExtensionPhoneState.Unknown;
	}

	static hasCallParty(contact: Contact): boolean {
		return isExtensionContact(contact) && Array.isArray(contact.callParties);
	}

	static getCallParties(contact: Contact): CallParty[] {
		if (isExtensionContact(contact) && Array.isArray(contact.callParties)) {
			return contact.callParties.filter((party: ExtensionParty): party is CallParty  => party?.name != null && party?.number != null);
		}
		return [];
	}

	static hasCallParties(contact: Contact): boolean {
		return this.getCallParties(contact).length > 0;
	}

	static hasOneCallParty(contact: Contact): boolean {
		return this.getCallParties(contact).length === 1;
	}

	static getFirstCallParty(contact: Contact): CallParty | undefined {
		const parties = this.getCallParties(contact);
		return parties.length > 0 ? parties[0] : undefined;
	}

	static isInConference(contact: ExtensionContact): boolean {
		return contact?.inConference ?? false;
	}

	static isInPause(contact: ExtensionContact): boolean {
		return contact?.paused ?? false;
	}

	static isBusy(contact: Contact): boolean {
		const extensionStatus = this.getExtensionStatus(contact);
		return extensionStatus ? ContactUtils.isExtensionInUse(extensionStatus) : true;
	}

	static isInActiveCall(contact: Contact): boolean {
		const extensionStatus = this.getExtensionStatus(contact);
		return extensionStatus ?
			ContactUtils.isExtensionInUse(extensionStatus) && this.hasOneCallParty(contact) :
			false;
	}

	static userIsInActiveCall(contact: Contact): boolean {
		return contact.lastActivity !== undefined;
	}

	static holdingCall(contact: ExtensionContact): boolean {
		return contact?.holdingCall ?? false;
	}

	static callMonitoring(contact: ExtensionContact): boolean {
		return contact?.callMonitoring ?? false;
	}

	static callRecording(contact: ExtensionContact): boolean {
		return contact?.callRecording ?? false;
	}

	static getStatusText(status: number | undefined): string {
		switch (status) {
			case ExtensionPhoneState.Unavailable: return 'Unavailable';
			case ExtensionPhoneState.Idle: return 'Idle';
			case ExtensionPhoneState.Ringing: return 'Ringing';
			case ExtensionPhoneState.InUse: return 'In Use';
			case ExtensionPhoneState.Unknown: return 'Unknown';
			default: return 'Unknown';
		}
	}

	static getStatusClass(status: number | undefined): string {
		switch (status) {
			case ExtensionPhoneState.Unavailable: return 'unavailable';
			case ExtensionPhoneState.Idle: return 'idle';
			case ExtensionPhoneState.Ringing: return 'ringing';
			case ExtensionPhoneState.InUse: return 'inuse';
			case ExtensionPhoneState.Unknown: return 'unknown';
			default: return 'unknown';
		}
	}

	static getContactSourceIcon(contact: Contact): string {
		switch (contact.source) {
			case ContactSource.MANUAL:
				return 'fas fa-phone';
			case ContactSource.LDAP:
				return 'fas fa-phone-flip';
			case ContactSource.OUTLOOK:
				return 'fas fa-envelope-open-text';
			case ContactSource.EXTENSION:
				return 'fas fa-headphones';
			default:
				return 'fas fa-user';
		}
	}

	static getExtensionContactStatusIconColor(contact: ExtensionContact): string {
		if (contact?.paused) {
			return 'is-paused';
		}

		if (contact?.holdingCall) {
			return 'holding-call';
		}

		if (contact?.callMonitoring) {
			return 'call-monitoring';
		}

		if (contact?.callRecording) {
			return 'call-recording';
		}

		if (contact?.inConference) {
			return 'in-conference';
		}

		return ''
	}

	static getInitials(name: string): string {
		return name
		.split(' ')
		.map((part: string) => part.charAt(0).toUpperCase())
		.slice(0, 2)
		.join('');
	}

	static getContactAriaLabel(contact: Contact): string {
		const extensionStatus = this.getExtensionStatus(contact);
		let label = `${contact.name}, status ${this.getStatusText(extensionStatus)}`;

		if (extensionStatus && isExtensionContact(contact)) {
			if (ContactUtils.isExtensionInUse(extensionStatus)) {
				if (contact?.callDuration) {
					label += `, currently on call for ${contact?.callDuration}`;
				}
			}

			if (contact?.lastActivity) {
				label += `, last active ${contact.lastActivity}`;
			}
		}

		return label;
	}

	static shouldShowCallInfo(contact: Contact): boolean {
		return isExtensionContact(contact) && this.isBusy(contact);
	}

	static shouldShowLastActivity(contact: Contact): boolean {
		return isExtensionContact(contact) &&
			contact.lastActivity !== undefined &&
			!this.isBusy(contact);
	}

	static getDisplayPhoneNumber(contact: Contact): string {
		if (isExtensionContact(contact) && contact.number) {
			return contact.number;
		}
		return contact.phoneNumber;
	}

	static getContactSourceLabel(contactSource: string, translate: TranslateService): string {
		switch (contactSource) {
			case ContactSource.MANUAL:
				return translate.instant('ASTIMAX.CONTACT.WIDGET.MANUAL');
			case ContactSource.LDAP:
				return 'LDAP Directory';
			case ContactSource.OUTLOOK:
				return 'Outlook Contacts';
			case ContactSource.EXTENSION:
				return translate.instant('ASTIMAX.CONTACT.WIDGET.EXTENSION');
			default:
				return 'Unknown Source';
		}
	}

	/**
	 * Extracts phone number from Astimax sipUser by removing the 'uc' prefix
	 */
	static extractPhoneNumberFromSipUser(sipUser: string | undefined): string | undefined {
		if (!sipUser) return undefined;

		// Remove 'uc' prefix (case insensitive)
		const match = sipUser.match(/^uc(.+)$/i);
		return match ? match[1] : sipUser;
	}

	/**
	 * Extracts the user's phone number from the user object
	 */
	static getUserPhoneNumber(user: IUser): string | undefined {
		if (!user?.astimaxAccount?.sipUser) {
			return undefined;
		}

		const phoneNumber = ContactUtils.extractPhoneNumberFromSipUser(user.astimaxAccount.sipUser);
		return phoneNumber ? ContactUtils.normalizePhoneNumber(phoneNumber) : undefined;
	}

	/**
	 * Normalizes phone numbers for comparison by removing common formatting
	 */
	static normalizePhoneNumber(phoneNumber: string): string {
		if (!phoneNumber) return '';
		// Remove all non-digit characters
		return phoneNumber.replace(/\D/g, '');
	}

	/**
	 * Checks if an extension contact matches the user's phone number
	 */
	static isContactCurrentUser(
		contact: ExtensionContact,
		userPhoneNumber: string | undefined
	): boolean {
		if (!userPhoneNumber || !contact.number) {
			return false;
		}

		const contactNumber = ContactUtils.normalizePhoneNumber(contact.number);
		return contactNumber === userPhoneNumber;
	}

	/**
	 * Marks extension contacts that belong to the current user
	 */
	static markCurrentUserInExtensionContacts(
		contacts: ExtensionContact[],
		userPhoneNumber: string | undefined
	): ExtensionContact[] {
		if (!userPhoneNumber) {
			return contacts;
		}

		return contacts.map(contact => ({
			...contact,
			isCurrentUser: ContactUtils.isContactCurrentUser(contact, userPhoneNumber)
		}));
	}

	/**
	 * Checks if the current user is in an active call with exactly one party and not holding
	 */
	static canTransferCall(currentUserContact: ExtensionContact | undefined): boolean {
		if (!currentUserContact) {
			return false;
		}

		// Must be in use (on a call)
		if (!currentUserContact.status || !ContactUtils.isExtensionInUse(currentUserContact.status)) {
			return false;
		}

		// Must not be holding the call
		if (currentUserContact.holdingCall) {
			return false;
		}

		// Must have exactly one call party (not in conference, not multiple calls)
		const callParties = ContactUtils.getCallParties(currentUserContact);
		if (callParties.length !== 1) {
			return false;
		}

		// Must not be in a conference
		return !currentUserContact.inConference;
	}

	/**
	 * Checks if a specific contact can be a transfer target
	 */
	static canBeTransferTarget(
		contact: Contact,
		currentUserContact: ExtensionContact | undefined
	): boolean {
		// Cannot transfer to yourself
		if (contact.isCurrentUser) {
			return false;
		}

		// Current user must be able to transfer
		if (!ContactUtils.canTransferCall(currentUserContact)) {
			return false;
		}

		// Only extension contacts can receive transfers
		return ContactUtils.isExtensionContact(contact);
	}

	static generateUUID(): string {
		return uuidv4();
	}

	/**
	 * Sorts contact references by their order property, fallback to array index
	 */
	static sortContactReferences(contacts: ContactReference[]): ContactReference[] {
		return [...contacts].sort((a: ContactReference, b: ContactReference) => {
			// If both have explicit order, use that
			if (a.order !== undefined && b.order !== undefined) {
				return a.order - b.order;
			}
			// If only one has explicit order, prioritize it
			if (a.order !== undefined && b.order === undefined) {
				return -1;
			}
			if (a.order === undefined && b.order !== undefined) {
				return 1;
			}
			// If neither has explicit order, maintain original array order
			return 0;
		});
	}

	/**
	 * Assigns order numbers to contact references based on their current array position
	 */
	static assignOrderNumbers(contacts: ContactReference[]): ContactReference[] {
		return contacts.map((contact: ContactReference, index: number) => ({
			...contact,
			order: index
		}));
	}

	/**
	 * Normalizes order numbers to ensure they are sequential and start from 0
	 */
	static normalizeOrderNumbers(contacts: ContactReference[]): ContactReference[] {
		const sortedContacts = this.sortContactReferences(contacts);
		return sortedContacts.map((contact: ContactReference, index: number) => ({
			...contact,
			order: index
		}));
	}

	/**
	 * Moves a contact to a specific position and updates all order numbers
	 */
	static moveContactToPosition(
		contacts: ContactReference[],
		contactToMove: ContactReference,
		newPosition: number
	): ContactReference[] {
		const workingContacts = [...contacts];

		// Remove the contact from its current position
		const currentIndex = workingContacts.findIndex((ref: ContactReference) =>
			ref.contactId === contactToMove.contactId && ref.source === contactToMove.source
		);

		if (currentIndex === -1) {
			return contacts; // Contact not found
		}

		// Remove from current position
		const [movedContact] = workingContacts.splice(currentIndex, 1);

		// Insert at new position
		workingContacts.splice(newPosition, 0, movedContact);

		// Reassign order numbers to all contacts
		return this.assignOrderNumbers(workingContacts);
	}

	/**
	 * Adds a new contact at a specific position
	 */
	static insertContactAtPosition(
		contacts: ContactReference[],
		newContact: ContactReference,
		position: number
	): ContactReference[] {
		const workingContacts = [...contacts];
		workingContacts.splice(position, 0, newContact);
		return this.assignOrderNumbers(workingContacts);
	}

	/**
	 * Removes a contact and normalizes remaining order numbers
	 */
	static removeContact(
		contacts: ContactReference[],
		contactToRemove: ContactReference
	): ContactReference[] {
		const filteredContacts = contacts.filter((ref: ContactReference) =>
			!(ref.contactId === contactToRemove.contactId && ref.source === contactToRemove.source)
		);
		return this.assignOrderNumbers(filteredContacts);
	}

	/**
	* Migrates existing ContactGroups that don't have order numbers
	* Should be called once during widget initialization
	*/
	static migrateContactGroupsToOrdered(groups: ContactGroup[]): ContactGroup[] {
		return groups.map(group => ({
			...group,
			contacts: this.assignOrderNumbers(group.contacts),
			updated: new Date()
		}));
	}

	/**
	 * Checks if a contact group needs order migration
	 */
	static needsOrderMigration(group: ContactGroup): boolean {
		return group.contacts.some((contact: ContactReference) => contact.order === undefined);
	}

	/**
	 * Sorts selected contacts based on the specified sort mode
	 * @param selectedContacts - Array of ContactReference objects to sort
	 * @param sortMode - Either 'selection' or 'alphabetical'
	 * @param allContacts - All available contacts for name lookup
	 * @returns Sorted array of ContactReference objects
	 */
	static sortSelectedContacts(
		selectedContacts: ContactReference[],
		sortMode: 'selection' | 'alphabetical',
		allContacts: Contact[]
	): ContactReference[] {
		const contacts = [...selectedContacts];

		if (sortMode === 'alphabetical') {
			// Sort alphabetically by contact name
			return contacts.sort((a: ContactReference, b: ContactReference) => {
				const contactA = allContacts.find((c: Contact) => c.contactId === a.contactId && c.source === a.source);
				const contactB = allContacts.find((c: Contact) => c.contactId === b.contactId && c.source === b.source);

				if (!contactA || !contactB) return 0;

				return contactA.name.localeCompare(contactB.name);
			});
		} else {
			// Sort by selection order (order property)
			return this.sortContactReferences(contacts);
		}
	}

	/**
	 * Gets the display position for a contact based on the current sort mode
	 * @param contactRef - The contact reference
	 * @param currentIndex - The current index in the displayed list
	 * @param sortMode - Either 'selection' or 'alphabetical'
	 * @returns 1-based position number for display
	 */
	static getContactDisplayPosition(
		contactRef: ContactReference,
		currentIndex: number,
		sortMode: 'selection' | 'alphabetical'
	): number {
		if (sortMode === 'alphabetical') {
			// In alphabetical mode, show alphabetical position
			return currentIndex + 1;
		} else {
			// In selection mode, show the actual selection order
			return (contactRef.order ?? currentIndex) + 1;
		}
	}

	/**
	 * Updates the selectedContacts array after drag and drop reordering
	 * Automatically switches to selection mode if user manually reorders
	 * @param sortedContacts - The currently displayed sorted contacts
	 * @param fromIndex
	 * @param toIndex
	 * @param originalContacts - The original selectedContactIds array
	 * @param currentSortMode - Current sort mode
	 * @returns Object with updated contacts and sort mode
	 */
	static handleContactReorder(
		sortedContacts: ContactReference[],
		fromIndex: number,
		toIndex: number,
		originalContacts: ContactReference[],
		currentSortMode: 'selection' | 'alphabetical'
	): { contacts: ContactReference[], sortMode: 'selection' | 'alphabetical' } {
		// Move contact in the sorted list
		const reorderedContacts = this.moveContactToPosition(
			sortedContacts,
			sortedContacts[fromIndex],
			toIndex
		);

		// If we were in alphabetical mode, switch back to selection order
		const newSortMode = currentSortMode === 'alphabetical' ? 'selection' : currentSortMode;

		return {
			contacts: reorderedContacts,
			sortMode: newSortMode
		};
	}
}
